import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import type { BlockerFunction } from 'react-router-dom';

type TConfirmResult = void | boolean | Promise<void | boolean>;

type TGuard = { enabled: boolean; handleConfirm: () => TConfirmResult; message: string };

// The router honors only the LAST registered blocker, and which instance that is depends on mount
// order - so every instance consults this shared registry, and any enabled guard blocks no matter
// which instance the router asks.
const guards = new Map<number, TGuard>();
let nextGuardId = 0;
let isHandlingBlock = false;

/**
 * Prevents navigation (both in-app and browser/tab close) until the user confirms.
 * If they confirm, calls handleConfirm and then continues.
 *
 * @param enabled   – turn on/off the guard
 * @param handleConfirm  – called if the user chooses to leave
 * @param message   – text shown in the confirmation dialog
 */
export const useLeaveGuard = (
  enabled: boolean,
  handleConfirm: () => TConfirmResult,
  message = 'You have unsaved changes. Are you sure you want to leave?'
) => {
  const idRef = useRef<number>();
  if (idRef.current === undefined) idRef.current = nextGuardId++;
  const guardRef = useRef<TGuard>();
  guardRef.current = { enabled, handleConfirm, message };
  guards.set(idRef.current, guardRef.current);

  useEffect(() => {
    const id = idRef.current!;
    guards.set(id, guardRef.current!);
    return () => {
      guards.delete(id);
    };
  }, []);

  // 1) In-app navigation blocking. Only when the page is actually being left: a citation or heading
  // link writes the query string and stays here, and there is nothing to warn about.
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        currentLocation.pathname !== nextLocation.pathname &&
        Array.from(guards.values()).some((guard) => guard.enabled),
      []
    )
  );
  useEffect(() => {
    if (blocker.state !== 'blocked' || isHandlingBlock) return;

    const active = Array.from(guards.values()).filter((guard) => guard.enabled);
    if (!active.length) {
      blocker.proceed();
      return;
    }

    if (!window.confirm([...new Set(active.map((guard) => guard.message))].join('\n\n'))) {
      blocker.reset();
      return;
    }

    isHandlingBlock = true;
    void (async () => {
      try {
        // Every enabled guard runs its confirm action; `false` means the operation that makes
        // leaving safe failed. Stay on the page so the user can retry without losing the state
        // this guard exists to protect.
        for (const guard of active) {
          if ((await guard.handleConfirm()) === false) {
            blocker.reset();
            return;
          }
        }
        blocker.proceed();
      } catch {
        blocker.reset();
      } finally {
        isHandlingBlock = false;
      }
    })();
  }, [blocker]);

  // 2) Browser/tab close blocking (calling `handleConfirm` is not possible because of browser security)
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled, message]);
};
